/* /collections/all only, catalogue-backed quick filters with native Shopify facets.
   Always stay on All Products. When selected collection has <=5000 products,
   its real Shopify facet form and product grid are fetched and updated together.
   Preserve ordinary Horizon grid layout, URL history, and a real-link fallback.
*/
(() => {
  function init() {
    const host = document.querySelector('.kgr-all-results.kgr-guided-on');
    const nav = host?.querySelector('[data-kgr-root]');
    if (!host || !nav) return;
    const choices = Array.from(nav.querySelectorAll('[data-kgr-choice]'));
    const mobileSort = nav.querySelector('[data-kgr-sort]');
    const status = nav.querySelector('[data-kgr-status]');
    // The permanent anchor is rendered before the quick-nav in main-collection.
    // It remains in place even if the desktop toolbar mounted the nav before init.
    const homeMarker = host.querySelector('#collection-products');
    const originPath = location.pathname;
    const de = document.documentElement.lang.toLowerCase().startsWith('de');
    let requestId = 0;
    let pending = null;
    let active = null;
    const sidebarSelector = '.collection-wrapper .facets-block-wrapper--vertical:not(theme-drawer)';
    const drawerSelector = 'theme-drawer#filters-drawer';
    const originalSidebar = host.querySelector(sidebarSelector)?.innerHTML || '';
    const originalDrawer = host.querySelector(drawerSelector)?.innerHTML || '';

    const chosen = handle => choices.find(el => el.dataset.kgrHandle === handle) || choices[0];
    const filterParams = params => Array.from(params.entries()).filter(([key]) => key.startsWith('filter.'));
    const format = value => Number(value).toLocaleString(de ? 'de-DE' : 'en-GB');

    function fromUrl() {
      const url = new URL(location.href);
      const rawPage = parseInt(url.searchParams.get('page') || '1', 10);
      return {
        handle: chosen(url.searchParams.get('category') || '').dataset.kgrHandle || '',
        sort: url.searchParams.get('sort_by') || 'manual',
        page: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
        filters: filterParams(url.searchParams)
      };
    }

    function sourceUrl(state) {
      const el = chosen(state.handle);
      const url = new URL(el.dataset.kgrFetchUrl || el.href, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('sort_by', state.sort);
      for (const [key, value] of state.filters) url.searchParams.append(key, value);
      if (state.page > 1) url.searchParams.set('page', String(state.page));
      const section = host.closest('[id^="shopify-section-"]');
      if (section) url.searchParams.set('section_id', section.id.replace('shopify-section-', ''));
      return url;
    }

    function visibleUrl(state) {
      const url = new URL(originPath + location.search, location.origin);
      for (const key of Array.from(url.searchParams.keys())) {
        if (['category', 'guided', 'page', 'sort_by'].includes(key) || key.startsWith('filter.')) url.searchParams.delete(key);
      }
      if (state.handle) url.searchParams.set('category', state.handle);
      if (state.sort !== 'manual') url.searchParams.set('sort_by', state.sort);
      if (state.page > 1) url.searchParams.set('page', String(state.page));
      for (const [key, value] of state.filters) url.searchParams.append(key, value);
      return url.pathname + url.search + (location.hash || '');
    }

    function mountQuickNav() {
      // Mobile: no reparenting into Horizon's fixed-height facets-toggle.
      // Chips and sorting have their own full-width row above the product grid.
      if (matchMedia('(max-width: 749px)').matches) {
        if (homeMarker.parentNode && nav.previousSibling !== homeMarker) {
          homeMarker.parentNode.insertBefore(nav, homeMarker.nextSibling);
        }
        nav.classList.remove('kgr-quick-nav--embedded');
        return;
      }
      const toolbar = host.querySelector('.facets--horizontal.facets-controls-wrapper');
      if (!toolbar) return;
      const before = Array.from(toolbar.children).find(el =>
        el.matches('sorting-filter-component') || el.querySelector('sorting-filter-component'));
      if (nav.parentElement !== toolbar) toolbar.insertBefore(nav, before || null);
      nav.classList.add('kgr-quick-nav--embedded');
    }

    function updateVisual(state, count, hasNative) {
      const selected = chosen(state.handle);
      for (const choice of choices) {
        const current = choice === selected;
        choice.classList.toggle('is-active', current);
        if (current) choice.setAttribute('aria-current', 'page');
        else choice.removeAttribute('aria-current');
      }
      const label = host.querySelector('[data-kgr-heading-count]');
      if (label) label.textContent = format(count) + (de ? ' Produkte' : ' products');
      host.classList.toggle('kgr-native-facets', hasNative);
      if (mobileSort) mobileSort.value = state.sort;
      host.querySelectorAll('select[name="sort_by"],input[type="radio"][name="sort_by"]').forEach(el => {
        if (el === mobileSort) return;
        if (el.tagName === 'SELECT') el.value = state.sort;
        else {
          el.checked = el.value === state.sort;
          const option = el.closest('[role="option"]');
          if (option) option.setAttribute('aria-selected', el.checked ? 'true' : 'false');
        }
      });
      if (status) {
        status.hidden = true;
        status.textContent = '';
      }
    }

    function updateNativeFacets(doc, state) {
      const destination = host.querySelector(sidebarSelector);
      const source = doc.querySelector(sidebarSelector);
      const drawer = host.querySelector(drawerSelector);
      const sourceDrawer = doc.querySelector(drawerSelector);
      const count = Number(chosen(state.handle).dataset.kgrCount || 0);
      const eligible = Boolean(state.handle && count > 0 && count <= 5000);
      const sourceFacetItems = source?.querySelector('.facets__filters-wrapper');
      const native = Boolean(eligible && sourceFacetItems?.children.length);
      if (destination) {
        if (native && source) destination.innerHTML = source.innerHTML;
        else {
          destination.innerHTML = originalSidebar;
          const inner = destination.querySelector('.facets--vertical');
          if (inner) {
            const note = document.createElement('div');
            note.className = 'kgr-no-native-filter-note';
            const strong = document.createElement('strong');
            strong.textContent = de ? 'Kategorien' : 'Browse categories';
            const copy = document.createElement('span');
            copy.textContent = de ? 'Wähle oben eine Kategorie, um die Produktauswahl einzugrenzen.' : 'Choose a category above to narrow the selection.';
            note.append(strong, copy);
            inner.prepend(note);
          }
        }
      }
      if (drawer) {
        const dialog = drawer.querySelector('dialog');
        const stayOpen = Boolean(dialog?.open);
        if (native && sourceDrawer) drawer.innerHTML = sourceDrawer.innerHTML;
        else drawer.innerHTML = originalDrawer;
        const newDialog = drawer.querySelector('dialog');
        if (stayOpen && newDialog && native) {
          try { newDialog.showModal(); } catch(e) { newDialog.setAttribute('open', ''); }
        }
      }
      return native;
    }

    async function navigate(state, push = true, scroll = false) {
      if (pending) pending.abort();
      const controller = new AbortController();
      pending = controller;
      const id = ++requestId;
      host.classList.add('kgr-is-loading');
      nav.setAttribute('aria-busy', 'true');
      if (status) {
        status.hidden = false;
        status.textContent = de ? 'Produkte werden geladen ...' : 'Loading products ...';
      }
      try {
        const response = await fetch(sourceUrl(state).toString(), {
          credentials: 'same-origin', headers: {'Accept':'text/html'}, signal:controller.signal
        });
        if (!response.ok) throw new Error('Product request failed: ' + response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (id !== requestId) return;
        const oldGrid = host.querySelector('#ResultsList');
        const freshGrid = doc.querySelector('results-list #ResultsList');
        if (!oldGrid || !freshGrid || !freshGrid.querySelector('.product-grid, .main-collection-grid__empty'))
          throw new Error('Shopify grid not found');
        oldGrid.replaceWith(document.importNode(freshGrid, true));
        const native = updateNativeFacets(doc, state);
        const sourceResults = doc.querySelector('.facets-block-wrapper--vertical:not(theme-drawer) [data-results-count]');
        const fallback = Number(chosen(state.handle).dataset.kgrCount || 0);
        const parsed = sourceResults ? Number(sourceResults.dataset.resultsCount) : NaN;
        const shownCount = Number.isFinite(parsed) ? parsed : fallback;
        active = state;
        updateVisual(state, shownCount, native);
        if (push) history.pushState({kgrAll:true}, '', visibleUrl(state));
        if (scroll) host.querySelector('#collection-products')?.scrollIntoView({behavior:'instant',block:'start'});
        mountQuickNav();
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (status) {
          status.hidden = false;
          status.textContent = de ? 'Produkte konnten nicht geladen werden. Bitte erneut versuchen.' : 'Unable to load products. Please try again.';
        }
        // Do not redirect shoppers to a different page on network or rendering failure.
        console.warn('All Products quick filter request failed', error);
      } finally {
        if (id === requestId) {
          host.classList.remove('kgr-is-loading');
          nav.removeAttribute('aria-busy');
        }
      }
    }

    // Native Shopify input controls are rendered from the selected <=5000 collection.
    // Stop the Horizon component's own route-based fetch and perform the same server
    // filtering against the underlying collection while staying on /collections/all.
    function facetsState(form) {
      const data = new URLSearchParams(new FormData(form));
      return {...active, page:1, filters:filterParams(data)};
    }

    document.addEventListener('change', event => {
      if (!(event.target instanceof Element) || !host.contains(event.target)) return;
      const input = event.target;
      if (input.matches('[name="sort_by"]')) {
        if (!input.value) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        navigate({...active, sort:input.value, page:1}, true, false);
        return;
      }
      if (!host.classList.contains('kgr-native-facets')) return;
      const form = input.closest('.facets-block-wrapper--vertical form');
      if (!form || !input.name?.startsWith('filter.')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      navigate(facetsState(form),true,false);
    },true);

    function clearFromURL(value) {
      if (!value) return null;
      const url = new URL(value, location.origin);
      return {...active, page:1, filters:filterParams(url.searchParams)};
    }
    function nativeClick(event) {
      if (!(event.target instanceof Element) || !host.contains(event.target)) return false;
      if (!host.classList.contains('kgr-native-facets')) return false;
      const remove = event.target.closest('.facets-block-wrapper--vertical facet-remove-component[data-url]');
      if (remove) {
        const state = clearFromURL(remove.dataset.url);
        if (!state) return false;
        event.preventDefault();
        event.stopImmediatePropagation();
        navigate(state,true,false);
        return true;
      }
      const clear = event.target.closest('.facets-block-wrapper--vertical facet-clear-component');
      if (clear) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const inputs = clear.closest('details, .facets__item, .facets__panel')?.querySelectorAll('[name^="filter."]') || [];
        const names = new Set(Array.from(inputs).map(el => el.name));
        navigate({...active,page:1,filters:active.filters.filter(([key])=>!names.has(key))},true,false);
        return true;
      }
      return false;
    }

    document.addEventListener('click', event => {
      if (!(event.target instanceof Element) || !host.contains(event.target)) return;
      const link = event.target.closest('[data-kgr-choice]');
      if (link && nav.contains(link)) {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.defaultPrevented) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        navigate({handle:link.dataset.kgrHandle || '',sort:active.sort,page:1,filters:[]},true,false);
        return;
      }
      if (nativeClick(event)) return;
      const pageLink = event.target.closest('#ResultsList .pagination a, #ResultsList .product-grid__continuation-link');
      if (!pageLink || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.defaultPrevented) return;
      const linkUrl = new URL(pageLink.href, location.origin);
      const page = parseInt(linkUrl.searchParams.get('page') || '1',10);
      if (linkUrl.origin !== location.origin || !Number.isFinite(page) || page < 1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      navigate({...active,page},true,true);
    },true);

    document.addEventListener('keydown', event => {
      if (!['Enter',' '].includes(event.key) || !(event.target instanceof Element) || !host.contains(event.target)) return;
      const target = event.target.closest('facet-remove-component[data-url],facet-clear-component');
      if (target) {
        nativeClick(event);
        return;
      }
      // Horizon also binds keyboard toggling directly to list-filter rows.
      // Intercept that path so keyboard users stay on All just like pointer users.
      if (!host.classList.contains('kgr-native-facets')) return;
      const row = event.target.closest('.facets-block-wrapper--vertical .facets__inputs-list-item');
      const input = row?.querySelector('input[type="checkbox"][name^="filter."]');
      const form = input?.closest('form');
      if (!form || event.target === input) return; // Native input fires its own change event.
      event.preventDefault();
      event.stopImmediatePropagation();
      input.checked = !input.checked;
      navigate(facetsState(form), true, false);
    },true);

    window.addEventListener('popstate', () => navigate(fromUrl(),false,false));
    window.addEventListener('resize', mountQuickNav);
    active=fromUrl();
    mountQuickNav();
    updateVisual(active, Number(chosen(active.handle).dataset.kgrCount || 0), false);
    // Initial deep links always hydrate from the real Shopify collection.
    if (active.handle || active.page > 1 || active.filters.length || active.sort !== 'manual') {
      navigate(active,false,false);
    } else {
      updateNativeFacets(document,active);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
})();
